import { type FC, useId, useState } from "react";

import { invalidateEntityHistory } from "@/api/admin/auditLog";
import { type IBrand, useUpdateBrandMutation } from "@/api/admin/brands";
import { QUERY_KEYS, RESPONSE_ANSWERS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import { ActionButtons, DetailsBlock, DetailsList } from "@/components/atoms";
import { toastError, toastSuccess, toastWarning } from "@/components/molecules";
import { FIELDS_MAPPING } from "@/utils/constants/allFieldsMapping";

import BrandFormContacts from "../../BrandFormContacts/BrandFormContacts";
import { EntityHistory } from "../../EntityHistory";

interface ContactsProps {
  data: IBrand;
  hasUpdatePermission?: boolean;
}

const Contacts: FC<ContactsProps> = ({ data, hasUpdatePermission }) => {
  const [isActive, setActive] = useState(false);
  const [isStepValid, setIsStepValid] = useState(false);
  const formID = useId();
  const { mutate: updateBrand, isPending } = useUpdateBrandMutation();

  // TODO: Instagram integration: value add
  const detailsContactData = [
    { title: "Email", value: data.email },
    { title: "Website (optional)", value: data.websiteUrl },
    { title: "Instagram integration", value: "-" },
    {
      title: "Social media",
      value: (data.socialLinks?.length ?? 0) > 0 ? "added" : "-",
    },
  ];

  const handleSubmit = (payload: IBrand) => {
    const { id, properties, ...restData } = { ...data, ...payload };

    updateBrand(
      {
        id: id || "",
        data: restData,
      },
      {
        onSuccess: async () => {
          toastSuccess("Brand was updated successfully!");
          setActive(false);
          await queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.brands, id],
          });
          await invalidateEntityHistory({ scope: id });
        },
        onError: error => {
          if (error.message === RESPONSE_ANSWERS.BAD_REQUEST) {
            toastWarning("Add contacts information to continue.", "Warning!");
          } else {
            toastError(
              "Oops! Something went wrong.",
              () => handleSubmit(payload),
              "Error"
            );
          }
        },
      }
    );
  };

  return (
    <DetailsBlock
      title="Contacts"
      isEdit={hasUpdatePermission}
      isActive={isActive}
      setActive={setActive}
    >
      <>
        {isActive ? (
          <div>
            <BrandFormContacts
              id={formID}
              defaultValues={data}
              onSubmit={handleSubmit}
              classes="px-0 pb-4"
              onValidityChange={setIsStepValid}
            />
            <ActionButtons
              formID={formID}
              click={() => setActive(false)}
              isStepValid={isStepValid}
              isPending={isPending}
            />
          </div>
        ) : (
          <DetailsList data={detailsContactData} />
        )}
        <EntityHistory
          entityId={data.id}
          entityName={data.nameEn}
          blockName="Contacts"
          wrapperClass="absolute right-13 top-4.5"
          mapKeyToName={FIELDS_MAPPING.brandContacts}
        />
      </>
    </DetailsBlock>
  );
};

export default Contacts;
